import React from 'react'
import { Link, useHistory } from 'react-router-dom'
import {
  AlertContainer,
  Container,
  ItemContainer,
  ToggleContainer
} from './NotificationPreferences.styles'
import { useTranslation, Trans } from 'i18n'
import type { User } from 'api/data.types'
import {
  ButtonIcon,
  FontIcon,
  ToggleSwitch,
  tokens
} from '@arvoreeducacao/bonsai-ds'
import { Spinner } from 'components/lib/loaders'
import { Hr } from 'screens/site/language-switcher/LanguageSwitcher.styles'
import useNotificationPreferences from 'utils/hooks/use-notification-preferences'
import useToast from 'utils/hooks/use-toast'
import { MY_ACCOUNT_PATH } from 'routes/paths'
import { useQuery } from '@tanstack/react-query'
import { teachersControllerGetTeacherClasses } from 'api.gen'
import { useUserInfo } from 'utils/hooks/use-userInfo'

type NotificationPreferencesProps = {
  user: User
}

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({
  user
}) => {
  const { toastMessage } = useToast()
  const { weeklyReadingReport, toggleNotification, isMutating, isFetching } =
    useNotificationPreferences()
  const { t } = useTranslation()
  const history = useHistory()
  const { user: currentUser } = useUserInfo()
  const teacherId = currentUser?.teacher?.id
    ? Number(currentUser.teacher.id)
    : undefined

  const { data: teacherClassesData, isLoading: isLoadingTeacherClasses } =
    useQuery({
      queryKey: ['teacher-classes', teacherId],
      queryFn: () =>
        teacherId
          ? teachersControllerGetTeacherClasses(teacherId)
          : Promise.reject(new Error('No teacher ID')),
      enabled: !!teacherId
    })

  const isLoading = isMutating || isFetching || isLoadingTeacherClasses

  const isInvalidState =
    !user.email || !teacherClassesData?.data?.classes?.length

  return (
    <Container>
      <h2>{t('Escolha as notificações que você gostaria de receber.')}</h2>

      <ItemContainer>
        <FontIcon color={tokens.colors.dark5} size={20} iconName="mail" />
        <div>
          <p>{t('Relatórios semanais de leitura')}</p>
          <span>
            <Trans
              i18nKey="Receba informações de leitura das <a>suas turmas.</a>"
              components={{
                a: <Link to={MY_ACCOUNT_PATH} />
              }}
            />
          </span>
        </div>
        <ToggleContainer>
          {isLoading ? (
            <Spinner color={tokens.colors.dark6} />
          ) : (
            <ToggleSwitch
              data-testid="toggle-switch"
              aria-label={t('Relatórios semanais de leitura')}
              disabled={isInvalidState}
              onChange={() => {
                if (isInvalidState) {
                  return
                }
                toggleNotification({
                  variables: {
                    notificationType: 'WEEKLY_READING_REPORT'
                  },
                  onCompleted: data => {
                    console.info('notification toggled', toastMessage)
                    toastMessage({
                      type: 'success',
                      message: data.toggleNotificationStatus.weeklyReadingReport
                        ? t('Notificação ativada com sucesso!')
                        : t('Notificação desativada com sucesso!')
                    })
                  }
                })
              }}
              active={!!weeklyReadingReport}
            />
          )}
        </ToggleContainer>
      </ItemContainer>

      <Hr />

      {isInvalidState && !isLoadingTeacherClasses && (
        <AlertContainer>
          <FontIcon color={tokens.colors.dark6} size={20} iconName="warning" />

          <div>
            <strong>
              {t(
                'Configure sua conta antes de ativar o recebimento dos relatórios semanais'
              )}
            </strong>
            <ul>
              <li>{t('Cadastre um e-mail válido')}</li>
              <li>{t('Selecione as turmas')}</li>
            </ul>
          </div>

          <ButtonIcon
            iconName={'caret-right'}
            aria-label={t('Configurar conta')}
            onClick={() => {
              history.replace(MY_ACCOUNT_PATH)
            }}
            theme={{ type: 'ghost', size: 'medium' }}
          />
        </AlertContainer>
      )}
    </Container>
  )
}

export default NotificationPreferences
